import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Broadcast, Plus, PencilSimple, Power, Question } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import { useRelativeTime } from '../../lib/time';
import { adminListOrgs, formatAdminError } from '../../lib/admin';
import {
  listGateways, createGateway, updateGateway, deactivateGateway, reactivateGateway, isNotDeployed, fmtMac,
} from '../../lib/gateways';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import { selectCls } from '../../components/admin/Field';
import { GatewayStatusChip } from '../../components/gateways/GatewayStatusChip';
import { GatewayForm } from '../../components/gateways/GatewayForm';

const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);

function GatewayRow({ g, t, rel, isSuper, onEdit, onToggle }) {
  return (
    <tr className={`border-t border-stone_line align-top ${g.deactivated ? 'opacity-60' : ''}`} data-testid={`gateway-row-${g.gateway_id}`}>
      <td className="px-5 py-3">
        <Link to={`/gateways/${g.gateway_id}`} className="text-sm font-medium text-ink hover:text-sage-700 whitespace-nowrap">{g.gateway_name || '—'}</Link>
        <div className="text-[11px] text-ink-muted">{g.gateway_hw_type || ''}</div>
      </td>
      {isSuper && <td className="px-5 py-3 font-mono text-xs text-ink-soft" data-testid={`gateway-mac-${g.gateway_id}`}>{fmtMac(g.gateway_mac_hex)}</td>}
      <td className="px-5 py-3 text-xs" data-testid={`gateway-assignment-${g.gateway_id}`}>
        {g.organization_id ? (
          <><div className="text-ink">{g.organization_name || '—'}</div><div className="text-ink-muted">{g.room_name || '—'}</div></>
        ) : <span className="text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-1 rounded-full bg-amber_warn-100 text-[#8a6224]">{t('gw_unassigned')}</span>}
      </td>
      <td className="px-5 py-3"><GatewayStatusChip status={g.status} testid={`gateway-status-${g.gateway_id}`} /></td>
      {isSuper && <td className="px-5 py-3 text-xs text-ink-soft" data-testid={`gateway-last-${g.gateway_id}`} title={g.last_message_at || ''}>{g.last_message_at ? rel(g.last_message_at) : t('gw_never')}</td>}
      <td className="px-5 py-3 text-sm text-ink tabular-nums" data-testid={`gateway-devices-${g.gateway_id}`}>{g.devices_seen_latest ?? '—'}</td>
      {isSuper && <td className="px-5 py-3 text-xs text-ink-soft tabular-nums" data-testid={`gateway-msgs-${g.gateway_id}`}>{g.messages_24h ?? '—'}</td>}
      {isSuper && <td className={`px-5 py-3 text-xs tabular-nums ${g.discard_rate >= 0.5 ? 'text-terracotta-700 font-semibold' : 'text-ink-soft'}`} data-testid={`gateway-discard-${g.gateway_id}`}>{pct(g.discard_rate)}</td>}
      <td className="px-5 py-3">
        <div className="flex items-center gap-1 justify-end">
          <button type="button" onClick={() => onEdit(g)} title={t('edit')} data-testid={`gateway-edit-${g.gateway_id}`} className="p-2 rounded-lg text-ink-soft hover:bg-bg-alt"><PencilSimple size={16} /></button>
          <button type="button" onClick={() => onToggle(g)} title={t(g.deactivated ? 'gw_reactivate' : 'gw_deactivate')} data-testid={`gateway-toggle-${g.gateway_id}`}
            className={`p-2 rounded-lg hover:bg-bg-alt ${g.deactivated ? 'text-sage-700' : 'text-terracotta-700'}`}><Power size={16} /></button>
        </div>
      </td>
    </tr>
  );
}

function Unregistered({ list, t }) {
  if (!list?.length) return null;
  return (
    <div className="bg-white border border-stone_line rounded-2xl shadow-soft p-6 mt-6" data-testid="gateways-unregistered">
      <div className="flex items-start gap-3 mb-4">
        <Question size={20} weight="duotone" className="text-ink-soft mt-0.5" />
        <div>
          <h2 className="font-heading text-xl text-ink">{t('gw_unregistered_title')}</h2>
          <p className="text-xs text-ink-muted mt-1 max-w-2xl">{t('gw_unregistered_desc')}</p>
        </div>
      </div>
      <ul className="divide-y divide-stone_line">
        {list.map((u) => (
          <li key={u.gw_id_hex} className="py-3 flex items-center gap-4 flex-wrap text-xs" data-testid={`gateway-unregistered-${u.gw_id_hex}`}>
            <span className="font-mono text-ink">{u.gw_id_text || u.gw_id_hex}</span>
            <span className="text-ink-muted">{t('gw_hours_active')}: {u.hours_active ?? '—'}</span>
            <span className="text-ink-muted">{t('gw_devices_seen')}: {u.devices_seen_max ?? '—'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminGateways() {
  const { t } = useI18n();
  const rel = useRelativeTime();
  const { user } = useAuth();
  const { current } = useOrg() || {};
  const isSuper = user?.is_super_admin === true;
  const [orgs, setOrgs] = useState([]);
  const [filter, setFilter] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | gateway
  const [formError, setFormError] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (isSuper) adminListOrgs().then(({ items }) => setOrgs(items)).catch(() => setOrgs([])); }, [isSuper]);
  useEffect(() => { if (!isSuper && current) setOrgs([current]); }, [isSuper, current]);

  const load = useCallback(() => {
    setError(null);
    const req = isSuper ? listGateways(filter ? { organization_id: filter } : {}, null) : listGateways({}, current?.id);
    req.then(setData).catch((e) => setError(e?.response?.status === 403 ? t('gw_scope_pending') : formatAdminError(t('gw_load_failed'), e)));
  }, [isSuper, filter, current?.id, t]);
  useEffect(() => { load(); }, [load]);

  const pending = (e) => (isNotDeployed(e) ? t('gw_backend_pending') : e?.response?.data?.detail || formatAdminError(t('gw_save_failed'), e));

  const save = async (body) => {
    setBusy(true); setFormError(null);
    try {
      const scope = isSuper ? null : current?.id;
      if (editing === 'new') await createGateway(body, scope); else await updateGateway(editing.gateway_id, body, scope);
      toast.success(t(editing === 'new' ? 'gw_created' : 'gw_saved'));
      setEditing(null); load();
    } catch (e) { setFormError(pending(e)); } finally { setBusy(false); }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      await (toggling.deactivated ? reactivateGateway : deactivateGateway)(toggling.gateway_id, isSuper ? null : current?.id);
      toast.success(t(toggling.deactivated ? 'gw_reactivated' : 'gw_deactivated'));
      setToggling(null); load();
    } catch (e) { toast.error(pending(e)); } finally { setBusy(false); }
  };

  const c = data?.counts?.activity || {};
  // Client-side filter too, in case the backend ignores organization_id.
  const shown = (data?.gateways || []).filter((g) => !filter || (filter === 'unassigned' ? !g.organization_id : g.organization_id === filter));
  return (
    <AdminPage title={t('gw_title')} description={t('gw_manage_sub')}>
      <div className="flex items-center gap-3 flex-wrap mb-4">
        {isSuper && (
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${selectCls} w-auto text-sm`} data-testid="gateways-org-filter">
            <option value="">{t('gw_filter_all')}</option>
            <option value="unassigned">{t('gw_unassigned')}</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
        {data && (
          <div className="flex items-center gap-3 flex-wrap text-xs" data-testid="gateways-summary">
            <Broadcast size={18} weight="duotone" className="text-sage-600" />
            <span className="text-ink">{t('gw_count_reporting', { n: c.reporting ?? 0 })}</span>
            <span className="text-terracotta-700">{t('gw_count_silent', { n: c.silent ?? 0 })}</span>
            <span className="text-ink-muted">{t('gw_count_never', { n: c.never_seen ?? 0 })}</span>
            {c.deactivated > 0 && <span className="text-ink-muted line-through decoration-1">{t('gw_count_deactivated', { n: c.deactivated })}</span>}
          </div>
        )}
        {isSuper && (
          <button type="button" onClick={() => { setFormError(null); setEditing('new'); }} data-testid="gateway-new-button"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700">
            <Plus size={16} weight="bold" />{t('gw_new')}
          </button>
        )}
      </div>
      {error && <div className="bg-terracotta-50 text-terracotta-700 rounded-xl p-4 text-sm mb-4" data-testid="gateways-error">{error}</div>}
      {!data && !error && <div className="text-ink-muted py-10 text-center">{t('loading')}</div>}
      {data && (
        <>
          <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-x-auto">
            <table className={`w-full text-left ${isSuper ? 'min-w-[1040px]' : 'min-w-[640px]'}`} data-testid="gateways-table">
              <thead className="bg-bg-alt text-[10px] uppercase tracking-[0.16em] text-ink-muted">
                <tr>
                  <th className="px-5 py-3 font-medium">{t('gw_col_name')}</th>
                  {isSuper && <th className="px-5 py-3 font-medium">MAC</th>}
                  <th className="px-5 py-3 font-medium">{t('gw_col_assignment')}</th>
                  <th className="px-5 py-3 font-medium">{t('gw_col_status')}</th>
                  {isSuper && <th className="px-5 py-3 font-medium">{t('gw_last_message')}</th>}
                  <th className="px-5 py-3 font-medium">{t('gw_devices_seen')}</th>
                  {isSuper && <th className="px-5 py-3 font-medium">{t('gw_col_msgs')}</th>}
                  {isSuper && <th className="px-5 py-3 font-medium">{t('gw_col_discard')}</th>}
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {shown.map((g) => (
                  <GatewayRow key={g.gateway_id} g={g} t={t} rel={rel} isSuper={isSuper}
                    onEdit={(x) => { setFormError(null); setEditing(x); }} onToggle={setToggling} />
                ))}
              </tbody>
            </table>
            {shown.length === 0 && <div className="p-10 text-center text-ink-muted" data-testid="gateways-empty">{t('gw_none_for_org')}</div>}
          </div>
          {isSuper && <Unregistered list={data.unregistered_reporters} t={t} />}
        </>
      )}
      {editing && (
        <GatewayForm gateway={editing === 'new' ? null : editing} orgs={orgs} isSuper={isSuper}
          defaultOrgId={isSuper ? (filter && filter !== 'unassigned' ? filter : '') : current?.id}
          knownHwTypes={[...new Map((data?.gateways || []).filter((g) => g.hw_type_id != null).map((g) => [g.hw_type_id, { id: g.hw_type_id, name: g.gateway_hw_type }])).values()]}
          onCancel={() => setEditing(null)} onSave={save} busy={busy} error={formError} />
      )}
      <ConfirmDialog open={!!toggling} busy={busy} danger={!toggling?.deactivated}
        title={t(toggling?.deactivated ? 'gw_reactivate_title' : 'gw_deactivate_title', { name: toggling?.gateway_name || '' })}
        description={t(toggling?.deactivated ? 'gw_reactivate_desc' : 'gw_deactivate_desc')}
        confirmLabel={t(toggling?.deactivated ? 'gw_reactivate' : 'gw_deactivate')}
        onConfirm={toggle} onCancel={() => setToggling(null)} />
    </AdminPage>
  );
}
